"use client";
import AuthenticationActionBar from "@components/auth/auth-action-bar/AuthenticationActionBar";
import { ContentLink } from "@components/cms/link/ContentLink";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { FooterEmailSubscription } from "@components/layouts/footer/footer-email-subscription/FooterEmailSubscription";
import { CountrySelector } from "@components/ui/country-selector/CountrySelector";
import { SocialMediaWidget } from "@components/ui/social-media-widget";
import type { FooterModel } from "@lib/contentful/mappers/mapFooter";
import { useCountriesSelector } from "@hooks/useCountrySelect";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ecco/ui/text";
import { Divider } from "@ecco/ui/divider";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { isEmpty } from "lodash-es";
import { styles } from "./styles";
import AccessibilityToggle from "@components/cms/accessibility/AccessibilityToggle";
import { FullBleed } from "@ecco/ui/full-bleed";

export interface FooterProps {
    footerData: FooterModel;
}
export const menuColumns = "menu-columns";
export const itemTitle = "item-title";

export const Footer = ({ footerData }: FooterProps) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { customerService, contactInformation, copyright, linkCollections, socialLinks } =
        footerData;

    const [countries, currentCountryId] = useCountriesSelector();

    const renewCookieLabel = getMicrocopy(CommonCMS.shared, CommonCMS.cookieRenew);

    const audioEyeId = process.env.NEXT_PUBLIC_AUDIOEYE_ID;
    const isAudioEnabled = Boolean(audioEyeId && audioEyeId.toLowerCase() !== "false");

    return (
        <Box data-testid="footer" sx={styles.container} id="footer">
            <FooterEmailSubscription />
            <FullBleed>
                <Divider sx={styles.divider} data-testid="divider" />
            </FullBleed>
            <footer>
                <Flex px={[4, 8, 8]}>
                    <Flex sx={styles.mainContent}>
                        <Box sx={styles.leftPart}>
                            {!isEmpty(contactInformation) && (
                                <Box data-testid="contact-text" sx={styles.contactInformation}>
                                    <RichText data={contactInformation} />
                                </Box>
                            )}
                            <Box data-testid="custom-service" sx={styles.customerService}>
                                <RichText data={customerService} />
                            </Box>
                        </Box>
                        {!isEmpty(linkCollections) &&
                            linkCollections.map((linkCollection, index) => (
                                <Box key={linkCollection.id} sx={styles.group}>
                                    <Text
                                        sx={styles.groupCapture}
                                        data-testid="footer-group-title"
                                        textTransform="uppercase"
                                    >
                                        {linkCollection.title}
                                    </Text>
                                    {index + 1 === linkCollections.length && (
                                        <Button
                                            variant="textLink"
                                            h="auto"
                                            data-testid="renewcookie"
                                            aria-label={renewCookieLabel}
                                            size="sm"
                                            sx={{ ...styles.link, lineHeight: "base" }}
                                            onClick={() => window.showCookieBanner()}
                                        >
                                            <span>{renewCookieLabel}</span>
                                        </Button>
                                    )}
                                    {linkCollection.links.map((link) => (
                                        <ContentLink
                                            key={link.id}
                                            link={link.contentLink}
                                            data-testid={itemTitle}
                                            sx={styles.link}
                                            {...(link.contentLink.kind === "modal"
                                                ? { variant: "textLink", size: "sm" }
                                                : {})}
                                        />
                                    ))}

                                    {isAudioEnabled && index + 1 === linkCollections.length && (
                                        <AccessibilityToggle variant={"textLink"} size={"sm"} />
                                    )}
                                </Box>
                            ))}
                    </Flex>
                </Flex>
                <Flex sx={styles.socialMediaContainer}>
                    <Box sx={styles.authContainer}>
                        <AuthenticationActionBar />
                    </Box>
                    <SocialMediaWidget socialLinks={socialLinks} />
                </Flex>
                <FullBleed>
                    <Divider sx={styles.divider} data-testid="divider" />
                </FullBleed>
                <Flex
                    px={[4, 8, 8]}
                    py={[3, 0]}
                    gap={[0, 4]}
                    justifyContent="space-between"
                    flexDirection={["column-reverse", "row"]}
                    alignItems={["flex-start", "center"]}
                >
                    <Flex sx={styles.privacyLocationContainer} data-testid="privacy-location">
                        <Box sx={styles.privacy} data-testid="footer-copyright-text">
                            {copyright}
                        </Box>
                    </Flex>
                    <Flex sx={styles.privacyLocationContainer}>
                        <CountrySelector
                            currentLocationId={currentCountryId}
                            locationList={countries}
                        />
                    </Flex>
                </Flex>
            </footer>
        </Box>
    );
};

export default Footer;
